export const HANDOFF_DATE_RANGES = [
  'all',
  'today',
  'last7Days',
  'thisWeek',
  'last30Days',
  'thisMonth',
] as const;

export type HandoffDateRange = (typeof HANDOFF_DATE_RANGES)[number];

export const HANDOFF_DATE_RANGE_TRANSLATION_KEYS: Record<HandoffDateRange, string> = {
  all: 'aiTasks.filter.all',
  today: 'aiTasks.filter.dateRange.today',
  last7Days: 'aiTasks.filter.dateRange.last7Days',
  thisWeek: 'aiTasks.filter.dateRange.thisWeek',
  last30Days: 'aiTasks.filter.dateRange.last30Days',
  thisMonth: 'aiTasks.filter.dateRange.thisMonth',
};

const DAY_IN_MS = 24 * 60 * 60 * 1000;

const RANGE_DAYS: Record<'last7Days' | 'last30Days', number> = {
  last7Days: 7,
  last30Days: 30,
};

const startOfDay = (timestamp: number): Date => {
  const date = new Date(timestamp);

  date.setHours(0, 0, 0, 0);

  return date;
};

const startOfWeek = (timestamp: number): Date => {
  const date = startOfDay(timestamp);

  date.setDate(date.getDate() - date.getDay());

  return date;
};

const startOfMonth = (timestamp: number): Date => {
  const date = startOfDay(timestamp);

  date.setDate(1);

  return date;
};

/** Returns the inclusive lower bound for `createdAtFrom`, or undefined when unfiltered. */
export const getHandoffDateRangeStart = (
  range: HandoffDateRange,
  now: number,
): string | undefined => {
  switch (range) {
    case 'all':
      return undefined;
    case 'today':
      return startOfDay(now).toISOString();
    case 'thisWeek':
      return startOfWeek(now).toISOString();
    case 'thisMonth':
      return startOfMonth(now).toISOString();
    default:
      return startOfDay(now - RANGE_DAYS[range] * DAY_IN_MS).toISOString();
  }
};

/**
 * Buckets unread handoff creation timestamps into the filter date ranges.
 * Timestamps are compared as epochs because the BFF may emit non-UTC offsets.
 */
export const getUnreadHandoffDateRanges = (
  createdAts: readonly string[],
  now: number,
): HandoffDateRange[] => {
  if (createdAts.length === 0) {
    return [];
  }

  const createdAtEpochs = createdAts.map((createdAt) => new Date(createdAt).getTime());

  return HANDOFF_DATE_RANGES.filter((range) => {
    const start = getHandoffDateRangeStart(range, now);

    if (start === undefined) {
      return true;
    }

    const startEpoch = new Date(start).getTime();

    return createdAtEpochs.some((createdAtEpoch) => createdAtEpoch >= startEpoch);
  });
};
